/**
 * Shared message-list reconcile helper used by every place that receives
 * incoming messages (designer Realtime, authed-client Realtime, guest poll).
 *
 *   - reconcile by `clientTempId` so an optimistic bubble is replaced
 *     in-place when the authoritative server row arrives;
 *   - dedupe by `id` because Realtime can re-deliver on reconnect and the
 *     guest poll is incremental but may overlap;
 *   - keep the list sorted by `createdAt` ascending.
 */
import type { Message } from '../types';

const sortByCreatedAt = (a: Message, b: Message) =>
  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();

export function mergeIncoming(prev: Message[], incoming: Message): Message[] {
  if (incoming.clientTempId) {
    const idx = prev.findIndex((m) => m.clientTempId === incoming.clientTempId);
    if (idx !== -1) {
      const next = prev.slice();
      next[idx] = { ...incoming, pending: false };
      return next;
    }
  }
  if (prev.some((m) => m.id === incoming.id)) return prev;
  return [...prev, { ...incoming, pending: false }].sort(sortByCreatedAt);
}

export function mergeIncomingBatch(prev: Message[], incoming: Message[]): Message[] {
  return incoming.reduce(mergeIncoming, prev);
}
